/**
 * nodeProfileRegistry - Per-node-type display profiles and global
 * Hidden/Metadata classifications.
 *
 * Direct port of sections 4-6 of `related-node-algorithm.md`. This module
 * is the single source of truth for:
 *   - Which node types are `Hidden`, `Metadata`, or rendered as a `Node`.
 *   - Which relationship types are `Hide`, `Cluster`, or `Show`.
 *   - Per-node-type priority bands and clustering thresholds ("profiles").
 *
 * The layout + selection pipelines consume these profiles; Cypher query
 * builders consume the Hidden sets to compile `$hiddenLabels` / `$hiddenRelTypes`.
 *
 * All pure data + pure lookup functions. No Jotai, no React.
 */

// ============================================================================
// Core classification sets
// ============================================================================

/** Node typenames that should never be rendered as standalone nodes. */
export const HIDDEN_NODE_TYPES: ReadonlySet<string> = new Set([
	// 1P
	'AtlassianTeamCustomFieldValue',
	'AtlassianTeamType',
	'LoomMeetingRecurrence',
	'LoomSpace',
	'OpsgenieTeam',
	// 3P
	'ExternalMessage',
	'ExternalConversation',
	'ExternalWorker',
	'ExternalPosition',
	'ExternalOrganisation',
	'ExternalCustomerContact',
	'ExternalCustomerOrgCategory',
	'ExternalSpace',
]);

/**
 * Node typenames that are aggregated onto their parent (comment counts,
 * priority icons, status history). They never appear standalone.
 */
export const METADATA_NODE_TYPES: ReadonlySet<string> = new Set([
	'ConfluenceComment',
	'JiraWorkItemComment',
	'LoomComment',
	'AtlassianHomeComment',
	'JiraWorkItemWorklog',
	'JiraWorkItemPriority',
	'JiraWorkItemStatus',
	'JiraWorkItemRemoteLink',
	'AtlassianGoalUpdate',
	'AtlassianProjectUpdate',
	'ConfluenceEmbed',
	'CompassComponentLink',
	'CompassScorecard',
	'AtlassianHomeTag',
	'ExternalComment',
	'ExternalBranch',
	'ExternalCommit',
	// Removed 2026-04 — these read-only 3P typenames were being dropped to
	// score 0 and never reaching the 3P rescue's pool. They legitimately
	// represent things the user touched (calendar meetings they were in,
	// links they bookmarked / sent) that should be eligible to surface
	// when the user has no other non-bitbucket 3P content competing.
	// Promotion past the type cap is still controlled by score + 3P
	// rescue, so this won't flood the view with calendar entries.
	//   - 'ExternalRemoteLink'   (was here, now scored)
	//   - 'ExternalCalendarEvent' (was here, now scored)
	// Added 2026-04 — HAR analysis (relevancy-lab) found these typenames
	// emitted as standalone discovery nodes that should be aggregated as
	// metadata onto their parent. Keeping them in the candidate set was
	// causing comment-walls and update-flood top-N for some users.
	'ConfluenceInlineComment',
	'JiraPlatformComment',
	'JiraServiceManagementComment',
	'TownsquareGoalUpdate',
	'TownsquareProjectUpdate',
]);

/**
 * True iff the given TWG typename is a "metadata" node — one that the
 * relevancy pipeline always scores to zero and aggregates onto its parent
 * rather than rendering as a standalone node.
 *
 * Used by `cypherQueryService.shouldQueryRelationship` to skip relationship
 * branches whose only target is metadata, so the cypher never asks the
 * graph for rows the algorithm is guaranteed to throw away.
 *
 * Lower-cased compare is fine because METADATA_NODE_TYPES uses the canonical
 * pascal-case typename (matches what `__typename` returns from cypher), but
 * relationship registry entries can include the same identifier in either
 * casing so we accept both.
 */
export function isMetadataTypename(typename: string | undefined | null): boolean {
	if (!typename) return false;
	if (METADATA_NODE_TYPES.has(typename)) return true;
	// Defensive: registry sometimes lowercases — match canonical form.
	for (const t of METADATA_NODE_TYPES) {
		if (t.toLowerCase() === typename.toLowerCase()) return true;
	}
	return false;
}

/**
 * Relationship types that are always dropped before scoring / layout.
 * Global hide list from section 5.
 */
const HIDDEN_REL_TYPES: ReadonlySet<string> = new Set([
	'created-comment',
	'created-worklog',
	'dismissed',
	'snapshotted',
	'trashed',
	'invited',
	'launched',
	'linked-via',
	'links-external-user',
	'authoritatively-linked',
	'has-external-position',
	'has-confluence-meeting-notes-folder',
	'approves',
	'submitted',
	'collaborates-on-inferred-project',
	'triggered-external-deployment',
	'links-external-customer-contact',
	'jira-work-item-has-jira-work-item-comment',
	'jira-work-item-has-jira-priority',
	'jira-work-item-has-changed-jira-priority',
	'jira-work-item-has-changed-jira-status',
	'jira-work-item-links-jira-work-item-remote-link',
	'jira-work-item-has-atlassian-autodev-job',
	'jira-work-item-links-support-escalation-entity',
	'loom-video-has-loom-video-comment',
	'atlassian-goal-has-atlassian-goal-update',
	'atlassian-goal-has-atlassian-home-tag',
	'atlassian-project-has-atlassian-project-update',
	'atlassian-project-has-atlassian-home-tag',
	'atlassian-team-has-atlassian-team-custom-field-value',
	'atlassian-team-is-atlassian-team-type',
	'atlassian-team-has-atlassian-agent',
	'jira-space-has-jira-board',
	'jira-space-has-jira-release-version',
	'jira-space-has-avp-dashboard',
	'jira-space-related-work-with-jira-space',
	'jira-space-shared-version-jira-space',
	'jira-space-shares-component-with-jsm-space',
	'jira-space-unlinked-external-branch',
	'jira-space-links-atlassian-autodev-job',
	'latest-alias',
	'node-type-links-node-type',
]);

/**
 * Relationship wildcard patterns to hide. Used when the rel type prefix
 * is enough to classify (avoids listing every variant).
 */
const HIDDEN_REL_PREFIXES: readonly string[] = [
	'compass-component-link-',
	'media-file-attached-to-',
	'repository-entity-is-',
	'third-party-remote-link-',
	'external-worker-',
	'external-position-',
	'talent-',
	'secoda-',
	'inferred-',
	'csm-',
	'topic-has-',
	'focus-ask-',
	'focus-change-proposal-',
	'focus-risk-',
	'focus-strategic-plan-',
	'assets-object-type-',
];

/** Relationship verbs that are always aggregated into cluster nodes. */
const ALWAYS_CLUSTER_REL_VERBS: ReadonlySet<string> = new Set([
	'viewed',
	'viewed-third-party',
	'favorited',
	'follows',
	'watches',
	'liked',
	'reacted-to',
	'can-view',
	'has-relevant',
	'shared-with',
]);

// ============================================================================
// Type boost — multiplier applied to target node during scoring
// ============================================================================

const TYPE_BOOST: Readonly<Record<string, number>> = {
	// 1P
	AtlassianGoal: 1.3,
	AtlassianProject: 1.2,
	JiraWorkItem: 1.0,
	ConfluencePage: 1.0,
	PullRequest: 1.0,
	AtlassianUser: 1.0,
	AtlassianTeam: 1.0,
	LoomVideo: 0.9,
	ConfluenceWhiteboard: 0.9,
	ConfluenceBlogpost: 0.9,
	FocusFocusArea: 0.8,
	CompassComponent: 0.8,
	AssetsObject: 0.8,
	ConfluenceSpace: 0.7,
	JiraSpace: 0.7,
	ConfluenceDatabase: 0.7,
	JiraSprint: 0.6,
	JiraVersion: 0.6,
	JiraBoard: 0.6,
	LoomMeeting: 0.6,
	// 3P
	ExternalDocument: 1.1,
	ExternalDesign: 1.1,
	ExternalWorkItem: 1.0,
	ExternalPullRequest: 1.0,
	ExternalService: 0.8,
	ExternalDeal: 0.8,
	ExternalCustomerOrg: 0.8,
	ExternalDeployment: 0.7,
	ExternalBuild: 0.7,
	ExternalVulnerability: 0.7,
	ExternalFeatureFlag: 0.7,
	ExternalRepository: 0.6,
	ExternalDashboard: 0.6,
	ExternalDataTable: 0.6,
	ExternalVideo: 0.6,
	ExternalProject: 0.6,
	// Read-only 3P content lifted out of METADATA in 2026-04. Modest
	// boosts keep them eligible for top-N when recently touched without
	// flooding the result set when the user has many of them.
	ExternalCalendarEvent: 0.7,
	ExternalRemoteLink: 0.6,
};

export function getTypeBoost(typename: string | undefined): number {
	if (!typename) return 1;
	return TYPE_BOOST[typename] ?? 1;
}

/** 3P (External*) typenames — used for 3P rescue detection. */
export function isThirdParty(typename: string | undefined): boolean {
	return !!typename && typename.startsWith('External');
}

/**
 * Type-family equivalence map for the per-type slot cap.
 *
 * The discovery layer surfaces the same logical content under multiple
 * Atlassian-side typenames (e.g. a Loom recording is returned as both
 * `LoomVideo` and `ExternalVideo`, the same Google Doc shows up as
 * `ExternalDocument` and a Confluence-bridged peer, etc.). If the cap
 * counts each typename independently, conceptually-similar items
 * collectively dominate the visible slots even though no single
 * typename exceeds its share.
 *
 * `getTypeFamily(typename)` collapses these visually-equivalent
 * typenames into a single bucket so the cap reflects what the user
 * actually perceives. Typenames not listed here use their own name as
 * the family (default 1:1 behaviour).
 */
const TYPE_FAMILY: Readonly<Record<string, string>> = {
	// Video — Loom-first vs External (3P-bridged) representation of the
	// same video asset. Both render as a video tile.
	LoomVideo: 'video',
	ExternalVideo: 'video',
	// Documents — Confluence pages, blog posts, whiteboards, databases
	// and the 3P-bridged "ExternalDocument" all read as "a document"
	// to the user.
	ConfluencePage: 'document',
	ConfluenceBlogPost: 'document',
	ConfluenceBlogpost: 'document',
	ConfluenceWhiteboard: 'document',
	ConfluenceDatabase: 'document',
	// Split out of the shared 'document' family in 2026-04 — when grouped
	// with ConfluencePage/BlogPost/Whiteboard the type cap (5 slots per
	// family) was completely consumed by Confluence pages (score ~43)
	// and Google Docs (score ~6) were evicted into typeCapOverflow
	// before the 3P rescue could see them. Giving Google Docs their own
	// family means the cap leaves at least one slot's worth of them
	// inside the rescue's pool.
	ExternalDocument: 'third-party-document',
	// Code — PRs and source-host PRs share a bucket.
	PullRequest: 'pull-request',
	ExternalPullRequest: 'pull-request',
	// Repos — 1P repo bridge vs raw 3P repo.
	ExternalRepository: 'repository',
	// Designs — Figma-and-friends.
	ExternalDesign: 'design',
	ExternalFigmaDesign: 'design',
	ExternalFigmaFile: 'design',
	// Work items — Jira and 3P trackers (Linear, Asana, etc.) all
	// render as "a ticket".
	JiraWorkItem: 'work-item',
	ExternalWorkItem: 'work-item',
};

/**
 * Resolve the cap-bucket family for a typename. Falls back to the
 * typename itself if no family is registered, preserving the prior
 * 1:1 behaviour for unmapped types.
 */
export function getTypeFamily(typename: string | undefined): string {
	if (!typename) return '';
	return TYPE_FAMILY[typename] ?? typename;
}

// ============================================================================
// Signal weights (Mode A) — activity-tag scoring
// ============================================================================

export const SIGNAL_WEIGHT: Readonly<Record<string, number>> = {
	created: 40,
	authored: 40,
	owned: 38,
	assigned: 38,
	reported: 35,
	updated: 30,
	contributed: 30,
	reviewer: 28,
	reviewed: 28,
	commented: 22,
	mentioned: 18,
	tagged: 18,
	// Added 2026-04 — HAR analysis verbs that previously fell through to
	// `?? 0`. Active verbs sit just below the `mentioned` band; passive
	// verbs match the `viewed` floor.
	linked: 25,
	collaborated: 25,
	subscribed: 8,
	// `snapshotted` is co-emitted by Confluence whenever the user views
	// or edits a page — it is essentially a duplicate of `viewed` /
	// `updated`. Pin it to 0 so it never inflates the signal stack on
	// its own; the same-day stacking dedup also collapses it.
	snapshotted: 0,
	followed: 12,
	watching: 12,
	reacted: 10,
	viewed: 8,
};

/**
 * Verbs that represent the user actively doing something to / with the
 * neighbour, as opposed to a passive observation (`viewed`, `followed`)
 * or system-emitted artefact (`snapshotted`, `subscribed`).
 *
 * Used by relevancy scoring to:
 *   - Pick the "primary" relationship for a candidate when multiple
 *     edges exist (active wins over passive when both are present).
 *   - Apply a passive-only score penalty.
 *
 * Keep this set in sync with `SIGNAL_WEIGHT` keys.
 */
export const ACTIVE_VERBS: ReadonlySet<string> = new Set([
	'created',
	'authored',
	'owned',
	'assigned',
	'reported',
	'updated',
	'contributed',
	'reviewer',
	'reviewed',
	'commented',
	'mentioned',
	'tagged',
	'linked',
	'collaborated',
]);

// ============================================================================
// Per-node-type display profiles (Section 6)
// ============================================================================

/**
 * A priority band in a node's profile. `relTypes` may be exact type names
 * or wildcard verbs (e.g. `created-*`) — callers match against both.
 *
 * `clusterThreshold`:
 *   - number: cluster when target count exceeds this number for that rel
 *   - 'always': every instance of this rel type clusters
 *   - 'never': never cluster regardless of count
 */
export type PriorityBand = {
	level: 1 | 2 | 3 | 4 | 5 | 6 | 7;
	relTypes: readonly string[];
	clusterThreshold: number | 'always' | 'never';
};

export type NodeProfile = {
	typename: string;
	bands: readonly PriorityBand[];
};

/** Weight applied to each priority level when scoring. */
export const PRIORITY_WEIGHT: Readonly<Record<1 | 2 | 3 | 4 | 5 | 6 | 7, number>> = {
	1: 40,
	2: 35,
	3: 30,
	4: 20,
	5: 15,
	6: 10,
	7: 5,
};

// ── Profiles ──────────────────────────────────────────────────────────────

const ATLASSIAN_USER: NodeProfile = {
	typename: 'AtlassianUser',
	bands: [
		{
			level: 1,
			relTypes: ['created-*', 'assigned-*', 'owns-*', 'reported-*'],
			clusterThreshold: 7,
		},
		{
			level: 2,
			relTypes: ['contributed-to-*', 'contributes-to-*', 'updated-*'],
			clusterThreshold: 5,
		},
		{ level: 3, relTypes: ['is-in-atlassian-team'], clusterThreshold: 5 },
		{ level: 4, relTypes: ['mentioned-in-*'], clusterThreshold: 3 },
		{
			level: 5,
			relTypes: [
				'viewed-*',
				'favorited-*',
				'follows-*',
				'watches-*',
				'liked-*',
				'reacted-to-*',
				'can-view-*',
			],
			clusterThreshold: 'always',
		},
	],
};

const JIRA_WORK_ITEM: NodeProfile = {
	typename: 'JiraWorkItem',
	bands: [
		{
			level: 1,
			relTypes: [
				'jira-work-item-links-external-pull-request',
				'jira-work-item-links-external-commit',
				'jira-work-item-links-external-deployment',
			],
			clusterThreshold: 10,
		},
		{
			level: 2,
			relTypes: [
				'jira-work-item-links-confluence-whiteboard',
				'jira-work-item-links-external-design',
				'jira-work-item-links-assets-object',
			],
			clusterThreshold: 5,
		},
		{
			level: 3,
			relTypes: [
				'jira-work-item-contributes-to-atlassian-goal',
				'jira-work-item-tracks-atlassian-project',
			],
			clusterThreshold: 'never',
		},
		{
			level: 4,
			relTypes: ['jira-work-item-blocks-jira-work-item', 'jira-work-item-links-jira-work-item'],
			clusterThreshold: 10,
		},
		{
			level: 5,
			relTypes: [
				'jira-work-item-links-external-build',
				'jira-work-item-links-external-feature-flag',
				'jira-work-item-links-external-vulnerability',
			],
			clusterThreshold: 5,
		},
		{ level: 6, relTypes: ['jira-work-item-changes-compass-component'], clusterThreshold: 3 },
		{ level: 7, relTypes: ['jira-work-item-has-child-jira-work-item'], clusterThreshold: 5 },
	],
};

const CONFLUENCE_PAGE: NodeProfile = {
	typename: 'ConfluencePage',
	bands: [
		{ level: 1, relTypes: ['user-created-*', 'user-contributed-to-*'], clusterThreshold: 8 },
		{
			level: 2,
			relTypes: ['confluence-page-links-jira-work-item', 'confluence-page-links-confluence-page'],
			clusterThreshold: 10,
		},
		{ level: 3, relTypes: ['loom-video-has-confluence-page'], clusterThreshold: 5 },
		{ level: 4, relTypes: ['loom-meeting-has-confluence-meeting-notes-page'], clusterThreshold: 3 },
		{ level: 5, relTypes: ['confluence-space-has-confluence-page'], clusterThreshold: 'never' },
	],
};

const CONFLUENCE_BLOGPOST: NodeProfile = {
	...CONFLUENCE_PAGE,
	typename: 'ConfluenceBlogpost',
};

const ATLASSIAN_GOAL: NodeProfile = {
	typename: 'AtlassianGoal',
	bands: [
		{ level: 1, relTypes: ['atlassian-goal-has-child-atlassian-goal'], clusterThreshold: 10 },
		{
			level: 2,
			relTypes: ['atlassian-project-contributes-to-atlassian-goal'],
			clusterThreshold: 10,
		},
		{
			level: 3,
			relTypes: ['jira-work-item-contributes-to-atlassian-goal'],
			clusterThreshold: 10,
		},
		{ level: 4, relTypes: ['user-owns-*', 'user-follows-*'], clusterThreshold: 5 },
		{
			level: 5,
			relTypes: ['atlassian-team-contributes-to-atlassian-goal'],
			clusterThreshold: 5,
		},
		{ level: 6, relTypes: ['focus-focus-area-has-work-entity'], clusterThreshold: 3 },
	],
};

const ATLASSIAN_PROJECT: NodeProfile = {
	typename: 'AtlassianProject',
	bands: [
		{
			level: 1,
			relTypes: ['atlassian-project-contributes-to-atlassian-goal'],
			clusterThreshold: 'never',
		},
		{
			level: 2,
			relTypes: [
				'atlassian-project-depends-on-atlassian-project',
				'atlassian-project-links-atlassian-project',
			],
			clusterThreshold: 10,
		},
		{ level: 3, relTypes: ['atlassian-project-links-entity'], clusterThreshold: 10 },
		{ level: 4, relTypes: ['user-owns-*', 'user-contributes-to-*'], clusterThreshold: 8 },
		{
			level: 5,
			relTypes: ['jira-work-item-tracks-atlassian-project'],
			clusterThreshold: 10,
		},
	],
};

const ATLASSIAN_TEAM: NodeProfile = {
	typename: 'AtlassianTeam',
	bands: [
		{ level: 1, relTypes: ['user-is-in-atlassian-team'], clusterThreshold: 15 },
		{ level: 2, relTypes: ['atlassian-team-links-space-entity'], clusterThreshold: 10 },
		{ level: 3, relTypes: ['atlassian-team-contributes-to-atlassian-goal'], clusterThreshold: 5 },
		{ level: 4, relTypes: ['atlassian-team-owns-compass-component'], clusterThreshold: 10 },
		{ level: 5, relTypes: ['atlassian-team-has-child-atlassian-team'], clusterThreshold: 5 },
		{ level: 6, relTypes: ['atlassian-team-works-on-external-repository'], clusterThreshold: 10 },
	],
};

const LOOM_VIDEO: NodeProfile = {
	typename: 'LoomVideo',
	bands: [
		{ level: 1, relTypes: ['user-created-loom-video'], clusterThreshold: 'never' },
		{ level: 2, relTypes: ['loom-video-has-confluence-page'], clusterThreshold: 5 },
		{ level: 3, relTypes: ['loom-meeting-has-loom-video'], clusterThreshold: 'never' },
		{ level: 4, relTypes: ['loom-video-shared-with-atlassian-user'], clusterThreshold: 'always' },
	],
};

const EXTERNAL_3P_OBJECT: NodeProfile = {
	typename: 'ExternalDocument', // representative — applied to multiple 3P types
	bands: [
		{ level: 1, relTypes: ['jira-work-item-links-*'], clusterThreshold: 10 },
		{ level: 2, relTypes: ['user-created-*', 'user-contributed-to-*'], clusterThreshold: 5 },
		{ level: 3, relTypes: ['jira-space-links-*'], clusterThreshold: 5 },
		{ level: 4, relTypes: ['user-viewed-third-party-*'], clusterThreshold: 'always' },
	],
};

const SPACE_CONTAINER: NodeProfile = {
	typename: 'ConfluenceSpace', // shared by JiraSpace
	bands: [
		{ level: 1, relTypes: ['atlassian-team-links-space-entity'], clusterThreshold: 'never' },
		{
			level: 2,
			relTypes: ['links-atlassian-goal', 'links-atlassian-project'],
			clusterThreshold: 5,
		},
		{
			level: 3,
			relTypes: ['links-external-repository', 'explicitly-links-external-repository'],
			clusterThreshold: 10,
		},
		{
			level: 4,
			relTypes: ['jira-space-has-jira-work-item', 'confluence-space-has-confluence-page'],
			clusterThreshold: 'always',
		},
		{
			level: 5,
			relTypes: ['links-compass-component', 'links-external-service'],
			clusterThreshold: 5,
		},
	],
};

const COMPASS_COMPONENT: NodeProfile = {
	typename: 'CompassComponent',
	bands: [
		{ level: 1, relTypes: ['atlassian-team-owns-compass-component'], clusterThreshold: 'never' },
		{ level: 2, relTypes: ['jsm-incident-impacts-compass-component'], clusterThreshold: 10 },
		{ level: 3, relTypes: ['jira-work-item-changes-compass-component'], clusterThreshold: 10 },
		{ level: 4, relTypes: ['compass-component-has-compass-component-link'], clusterThreshold: 5 },
	],
};

const FOCUS_FOCUS_AREA: NodeProfile = {
	typename: 'FocusFocusArea',
	bands: [
		{ level: 1, relTypes: ['focus-focus-area-has-child-focus-focus-area'], clusterThreshold: 10 },
		{ level: 2, relTypes: ['focus-focus-area-has-work-entity'], clusterThreshold: 10 },
		{
			level: 3,
			relTypes: ['user-owns-focus-focus-area', 'user-created-focus-focus-area'],
			clusterThreshold: 5,
		},
	],
};

const ASSETS_OBJECT: NodeProfile = {
	typename: 'AssetsObject',
	bands: [{ level: 1, relTypes: ['jira-work-item-links-assets-object'], clusterThreshold: 10 }],
};

const DEFAULT_PROFILE: NodeProfile = {
	typename: '__default__',
	bands: [
		{ level: 1, relTypes: ['*'], clusterThreshold: 10 },
		{
			level: 2,
			relTypes: ['user-created-*', 'user-contributed-*', 'user-assigned-*'],
			clusterThreshold: 5,
		},
		{ level: 3, relTypes: ['*cluster*'], clusterThreshold: 'always' },
	],
};

// ── Registry ──────────────────────────────────────────────────────────────

const PROFILES: Readonly<Record<string, NodeProfile>> = {
	AtlassianUser: ATLASSIAN_USER,
	JiraWorkItem: JIRA_WORK_ITEM,
	ConfluencePage: CONFLUENCE_PAGE,
	ConfluenceBlogpost: CONFLUENCE_BLOGPOST,
	AtlassianGoal: ATLASSIAN_GOAL,
	AtlassianProject: ATLASSIAN_PROJECT,
	AtlassianTeam: ATLASSIAN_TEAM,
	LoomVideo: LOOM_VIDEO,
	ExternalDocument: EXTERNAL_3P_OBJECT,
	ExternalDesign: EXTERNAL_3P_OBJECT,
	ExternalPullRequest: EXTERNAL_3P_OBJECT,
	ExternalWorkItem: EXTERNAL_3P_OBJECT,
	ConfluenceSpace: SPACE_CONTAINER,
	JiraSpace: SPACE_CONTAINER,
	CompassComponent: COMPASS_COMPONENT,
	FocusFocusArea: FOCUS_FOCUS_AREA,
	AssetsObject: ASSETS_OBJECT,
};

export function getProfile(typename: string | undefined): NodeProfile {
	if (!typename) return DEFAULT_PROFILE;
	return PROFILES[typename] ?? DEFAULT_PROFILE;
}

// ============================================================================
// Relationship matching helpers
// ============================================================================

/**
 * Matches a relationship type against a profile pattern. Patterns may be:
 *   - exact match: `'jira-work-item-links-external-pull-request'`
 *   - suffix wildcard: `'created-*'` matches `'user-created-jira-work-item'`
 *                        and `'created-jira-work-item'`
 *   - prefix wildcard: `'*-created'` (rarely used)
 *   - `'*'` matches anything.
 */
function matchesRelPattern(relType: string, pattern: string): boolean {
	if (pattern === '*') return true;
	if (pattern === relType) return true;
	if (pattern.endsWith('-*')) {
		const prefix = pattern.slice(0, -2);
		return relType === prefix || relType.includes(prefix);
	}
	if (pattern.startsWith('*-')) {
		const suffix = pattern.slice(2);
		return relType.endsWith(suffix);
	}
	if (pattern.includes('*')) {
		const [left, right] = pattern.split('*', 2);
		return relType.startsWith(left) && relType.endsWith(right);
	}
	return false;
}

/** True if this relationship type should never be individually rendered. */
export function isHiddenRelType(relType: string): boolean {
	if (HIDDEN_REL_TYPES.has(relType)) return true;
	for (const prefix of HIDDEN_REL_PREFIXES) {
		if (relType.startsWith(prefix)) return true;
	}
	return false;
}

/** True if this rel type is an "always cluster" verb regardless of profile. */
export function isAlwaysClusterRel(relType: string): boolean {
	for (const verb of ALWAYS_CLUSTER_REL_VERBS) {
		if (relType === verb || relType.startsWith(`${verb}-`) || relType.includes(`-${verb}-`)) {
			return true;
		}
	}
	return false;
}

/**
 * Finds the matching priority band for a relationship type against a
 * profile. Returns `null` if the rel type matches none of the profile's
 * patterns (caller should fall back to the default profile or drop).
 */
export function findPriorityBand(profile: NodeProfile, relType: string): PriorityBand | null {
	for (const band of profile.bands) {
		for (const pattern of band.relTypes) {
			if (matchesRelPattern(relType, pattern)) {
				return band;
			}
		}
	}
	return null;
}
